import {Column, Table, AutoSizer} from 'react-virtualized';
import { store } from '../../../stores/Store';
import { Button, LinearProgress } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { format, isValid } from 'date-fns';
import React from 'react';
import { Icons } from '../../../misc/Icons';
import { ColumnHeader, ColumnHeaderAccount, ColumnHeaderNotificationEmail, ColumnHeaderTemplate } from './QuestionnaireTableHeaders';
import { Theme } from '../../../misc/Theme';
import { UTCDate } from '@date-fns/utc';


interface QuestionnaireTableProps {
    width?: string;
    height?: string;
    onRowClick?: Function;
    onCheckboxClick?: Function;
    size?: "sm" | "md";
    highlightedOptions?: string[];
    selectedOptions?: string[];
}


// const updatePercentageComplete = async (uid) => {

//     var alert = Alert({message: "Getting progress for " + uid, persist: true})

//     const {getPageList} = store.QuestionnaireStore

//     await Ajax.Questionnaire.Get(uid).then(async (response) => {
        
//         var questionnaire = response.data
//         var template = JSON.parse(questionnaire.template.json)
//         var answers = JSON.parse(questionnaire.answers)
//         var pagesSubmitted = getPageList(template.pages, answers, true)
//         var pagesRemaining = getPageList(template.pages, answers, false)

//         var totalPages = pagesSubmitted.length + pagesRemaining.length
//         var percentageComplete = Math.round(pagesSubmitted.length / totalPages * 100)

//         var newQuestionnaire = {...questionnaire, percentageComplete: percentageComplete}

//         Ajax.Questionnaire.Update(newQuestionnaire).then(async () => {
//             questionnaire.percentageComplete = percentageComplete
//             store.QuestionnaireStore.getQuestionnaires()
//         }).then(() => {
//             alert.dismiss()
//         })
//     }).catch(() => {
//         Alert({message: "Failed to update progress for " + uid, color: "error"})
//     })
// }

export default observer(function QuestionnaireTable ({
    onRowClick = (uid: string) => {},
    onCheckboxClick = (uid: string) => {},
    height = "400px",
    size = "md",
    highlightedOptions = [],
    selectedOptions = null
}: QuestionnaireTableProps) {

    const { folderNode } = store.NodeStore
    const { loading, questionnaires, filterQuestionnaires, getQuestionnaires, setSearchAccountUids, setSearchString, orderPhrase } = store.QuestionnaireStore
    const minWidth = 1450
    var rowHeight = size === "md" ? 40 : 27
    var list = filterQuestionnaires(questionnaires)

    React.useEffect(() => {
        if (!questionnaires?.length) {
            getQuestionnaires()
        }

        // Reset template filter
        // setSearchTemplateKeys(null)

        // Filter questionnaires by account
        if (!store.ToolStore.tool) {
            
            setSearchString("")
            var searchAccountUids = null
            var accountRef = store.NodeStore.breadcrumb.find(nr => nr.uid.includes("ACCOUNT"))
            
            if (accountRef) {
                var account = store.NodeStore.accounts.find(a => a.uid === accountRef.uid && ["sponsor", "client"].includes(a.accountType))
                if (account?.advisorUid) {
                    searchAccountUids = [account.advisorUid]
                }
            }

            setSearchAccountUids(searchAccountUids)
        }
    }, [])

    const formatDate = (dateString, placeholder = "") => {
        if (!dateString) return <span className="font-grey">{placeholder}</span>
        var date = new UTCDate(dateString)
        return isValid(date) ? format(date, "dd/MM/yyyy H:mm") : <span className="font-grey">{placeholder}</span>
    }

    const CellWrapper = ({children, uid}) => {

        var highlight = highlightedOptions.includes(uid)

        return (
            <div className="trunc-ellipsis" style={{fontWeight: highlight ? 600 : 400, display: "flex", alignItems: "center", gap: "5px"}}>
                {children}
            </div>
        )
    }

    // Highlight any questionnaires which match tags with the caseNode
    var searchTags = folderNode?.tags && Object.keys(folderNode.tags).length ? folderNode.tags : null
    if (searchTags) {
        var taggedQuestionnaires = questionnaires.filter(q => {
            if (!q.tags) return false

            var match = true

            for (var i = 0; i < Object.keys(searchTags).length; i++) {
                var key = Object.keys(searchTags)[i]
                console.log(q.tags[key], searchTags[key])
                if (q.tags[key] !== searchTags[key]) {
                    match = false
                    break
                }
            }
            
            return match
        })
        highlightedOptions = [...highlightedOptions, ...taggedQuestionnaires.map(q => q.uid)]
    }

    // Insert highlighted options at the top
    var hightedQuestionnaires = highlightedOptions?.length && questionnaires?.length ? questionnaires?.filter(q => highlightedOptions.includes(q.uid)) : []
    if (hightedQuestionnaires?.length) {
        list = [...hightedQuestionnaires, ...list.filter(q => !highlightedOptions.includes(q.uid))]
    }

    return (
        <div className={`rv-table-wrapper rv-table-${size}`} style={{minHeight: height}}>
            <small style={{marginBottom: "5px"}}>
                {list?.length} results, {orderPhrase}.
            </small>

            <AutoSizer>
                {({height, width}) => (
                    <Table
                        width={width < minWidth ? minWidth : width}
                        height={height}
                        headerHeight={rowHeight}
                        rowHeight={rowHeight}
                        rowCount={list?.length}
                        rowGetter={({index}) => list[index]}
                        onRowClick={({event, index, rowData}) => {
                            // Don't do anything if click was on a input field
                            if (event.target.nodeName === "DIV") {
                                onRowClick(rowData.uid)
                            }
                        }} >

                        {Array.isArray(selectedOptions) ? (
                            <Column
                                dataKey=""
                                width={40}
                                headerRenderer={() => <></>}
                                cellRenderer={({rowData}) => {
                                    
                                    var number = selectedOptions.indexOf(rowData.uid)
                                    var disabled = number < 0 && selectedOptions?.length > 5
                                    var style = {fontSize: "18px"}

                                    return (
                                        <Button 
                                            disabled={disabled}
                                            style={{padding: 0, margin: 0, width: 0, opacity: disabled ? 0.4 : 1}} 
                                            color="info" 
                                            onClick={(e) => {
                                                onCheckboxClick(rowData.uid)
                                        }}>
                                            <CheckIcon index={number} />
                                        </Button>
                                    )
                                }} />
                        ) : null}

                        {store.AppStore.sessionInfo.accountPreferences?.integration_api_enabled ? (
                            <Column
                                dataKey=""
                                width={68}
                                headerRenderer={() => <ColumnHeader value="Preloaded" />}
                                cellRenderer={({rowData}) => (
                                    <CellWrapper uid={rowData.uid}>
                                        {rowData.preloadedAnswersCount ? (
                                            <>{rowData.preloadedAnswersCount}</>
                                        ) : <span className="font-grey">-</span>}
                                    </CellWrapper>
                                )} />
                        ) : null}
                        
                        <Column
                            dataKey=""
                            width={70}
                            headerRenderer={() => <ColumnHeader value="Submitted" />}
                            cellRenderer={({rowData}) => (
                                <CellWrapper uid={rowData.uid}>
                                    {rowData.percentageComplete ? (
                                        <>{rowData.percentageComplete}%</>
                                    ) : <span className="font-grey">0%</span>}

                                    {rowData.locked ? <Icons.Lock style={{fontSize: "12px", marginLeft: "auto", color: Theme.palette.success.main}} /> : null}
                                </CellWrapper>
                            )} />
                        
                        <Column
                            dataKey=""
                            width={150}
                            headerRenderer={() => <ColumnHeader value="displayName" />}
                            cellRenderer={({rowData}) => (
                                <CellWrapper uid={rowData.uid}>
                                    {rowData.displayName}
                                </CellWrapper>
                            )} />

                        <Column
                            dataKey=""
                            width={180}
                            headerRenderer={() => <ColumnHeaderTemplate />}
                            cellRenderer={({rowData}) => (
                                <CellWrapper uid={rowData.uid}>
                                    {rowData.template.displayName}
                                </CellWrapper>
                            )} />

                        {store.AppStore.checkJwtRole("sysuser") ? (
                            <Column
                                dataKey=""
                                width={140}
                                headerRenderer={() => <ColumnHeaderAccount label="Account" />}
                                cellRenderer={({rowData}) => {

                                    var advisor = store.NodeStore.accounts.find(a => a.advisorUid === rowData.respondent?.advisorUid)
                                    if (["sponsor", "client"].includes(advisor?.accountType)) {
                                        advisor = store.NodeStore.accounts.find(a => a.uid === advisor.parent.uid)
                                    }

                                    return (
                                        <CellWrapper uid={rowData.uid}>
                                            {advisor ? advisor.label : <span className="font-grey">No access</span>}
                                        </CellWrapper>
                                    )
                                }} />
                        ) : null}

                        {!store.AppStore.checkJwtRoles(["client_account", "sponsor_account"], false) ? (
                            <Column
                                dataKey=""
                                width={140}
                                headerRenderer={() => <ColumnHeaderAccount label="Sponsor" />}
                                cellRenderer={({rowData}) => {

                                    var advisor = store.NodeStore.accounts.find(a => ["sponsor", "client"].includes(a.accountType) && a.advisorUid === rowData.respondent?.advisorUid)

                                    return (
                                        <CellWrapper uid={rowData.uid}>
                                            {advisor ? advisor.label : <span className="font-grey">Unassigned</span>}
                                        </CellWrapper>
                                    )
                                }} />
                        ) : null}

                        <Column
                            dataKey=""
                            width={150}
                            headerRenderer={() => <ColumnHeaderNotificationEmail />}
                            cellRenderer={({rowData}) => (
                                <CellWrapper uid={rowData.uid}>
                                    {rowData.notificationEmail ? rowData.notificationEmail : <span className="font-grey">Unassigned</span>}
                                </CellWrapper>
                            )} />

                        <Column
                            dataKey=""
                            width={150}
                            headerRenderer={() => <ColumnHeader value="respondent" />}
                            cellRenderer={({rowData}) => (
                                <CellWrapper uid={rowData.uid}>
                                    {rowData.respondent?.respondentEmail}
                                </CellWrapper>
                            )} />

                        <Column
                            dataKey=""
                            width={140}
                            headerRenderer={() => <ColumnHeader value="updatedAt" />}
                            cellRenderer={({rowData}) => (
                                <CellWrapper uid={rowData.uid}>
                                    {formatDate(rowData.updatedAt, "Not started")}
                                </CellWrapper>
                            )} />

                        <Column
                            dataKey=""
                            width={140}
                            headerRenderer={() => <ColumnHeader value="createdAt" />}
                            cellRenderer={({rowData}) => (
                                <CellWrapper uid={rowData.uid}>
                                    {formatDate(rowData.createdAt)}
                                </CellWrapper>
                            )} />

                        <Column 
                            dataKey="" 
                            width={35} 
                            headerRenderer={(rowData) => <CellWrapper uid={rowData.uid}><></></CellWrapper>} 
                            cellRenderer={(data) => (
                                <div style={{marginLeft: "auto"}}></div>
                            )} />
                    </Table>
                )}
            </AutoSizer>

            {loading ? <LinearProgress color="secondary" style={{marginTop: `${rowHeight}px`}} /> : null}
        </div>
    )
})




export function CheckIcon ({index = null}) {
    
    const style = {fontSize: "18px"}
    
    switch (index) {
        case 0:
            return <Icons.One style={{...style, color: "#333333"}} />
        case 1:
            return <Icons.Two style={{...style, color: "#555555"}} />
        case 2:
            return <Icons.Three style={{...style, color: "#777777"}} />
        case 3:
            return <Icons.Four style={{...style, color: "#888888"}} />
        case 4:
            return <Icons.Five style={{...style, color: "#999999"}} />
        case 5:
            return <Icons.Six style={{...style, color: "#aaaaaa"}} />
        case "aora":
            return <Icons.Enquiry style={{...style, color: "#333333"}} />
        case "enquiry":
            return <Icons.Enquiry style={{...style, color: "#333333"}} />
        default:
            return <Icons.Checkbox style={{...style, color: "#333333"}} />
    }
}